import { formatFileSize } from '../../utils/formatters';
import type { EDiscoveryExport, EDiscoverySearchRun, EDiscoverySource } from '../../types';

/** Purview statuses whose export has files to import. */
const IMPORTABLE_STATUSES = new Set(['succeeded', 'partiallySucceeded']);

export function isImportable(exp: Pick<EDiscoveryExport, 'status' | 'expired'>): boolean {
  return !exp.expired && IMPORTABLE_STATUSES.has(exp.status ?? 'succeeded');
}

export const EXPORT_STATUS_LABELS: Record<string, string> = {
  notStarted: 'Queued',
  running: 'Running',
  failed: 'Failed',
  partiallySucceeded: 'Partial',
};

/** The tag on an export that can't be ticked ("Expired", "Running", …), or null. */
export function exportBadge(exp: Pick<EDiscoveryExport, 'status' | 'expired'>): string | null {
  if (exp.expired) return 'Expired';
  if (isImportable(exp) || !exp.status) return null;
  return EXPORT_STATUS_LABELS[exp.status] ?? exp.status;
}

/** "date · author · N packages · size" — packages/size only once the export has finished. */
export function exportMeta(exp: EDiscoveryExport): string {
  const parts: string[] = [];
  if (exp.created_at) parts.push(new Date(exp.created_at).toLocaleString());
  if (exp.created_by) parts.push(exp.created_by);
  if (isImportable(exp)) {
    parts.push(`${exp.package_count} ${exp.package_count === 1 ? 'package' : 'packages'}`);
    if (exp.total_size) parts.push(formatFileSize(exp.total_size));
  }
  return parts.join(' · ');
}

const ACTIVE_STAGES = new Set(['estimating', 'exporting', 'waiting']);

/** A search RedactIt runs in the system is still going (sc-11767). */
export function searchRunActive(run: EDiscoverySearchRun | null | undefined): boolean {
  return !!run && ACTIVE_STAGES.has(run.stage);
}

/**
 * Where a search RedactIt runs in the system stands (sc-11767), as a step of
 * three (search, export, import) and a short line that fits the documents panel:
 * step 1 "Searching… 40%", step 2 "Found 11 items (618.4 KB). Exporting…".
 */
export function searchRunStep(run: EDiscoverySearchRun | null | undefined): { step: 1 | 2 | 3; line: string } {
  const percent = run?.percent ? ` ${run.percent}%` : '';
  if (!run || run.stage === 'estimating') return { step: 1, line: `Searching…${percent}` };
  const count = run.item_count ?? 0;
  const found = `Found ${count.toLocaleString()} ${count === 1 ? 'item' : 'items'}`
    + (run.size ? ` (${formatFileSize(run.size)})` : '');
  if (run.stage === 'exporting') return { step: 2, line: `${found}. Exporting…${percent}` };
  if (run.stage === 'waiting') return { step: 2, line: 'Export ready. Imported after the current import.' };
  return { step: 3, line: `${found}.` };
}

/** "Step 1 of 3 · Searching in Purview… 40%": one line for the wizard's source panel. */
export function searchRunLine(run: EDiscoverySearchRun | null | undefined, systemLabel: string): string {
  const { step, line } = searchRunStep(run);
  const text = step === 1 ? line.replace('Searching…', `Searching in ${systemLabel}…`) : line;
  return `Step ${step} of 3 · ${text}`;
}

/** " · 2 more exports queued" (waiting for the import that's going), or "" when nothing is queued. */
function queuedSuffix(source: Pick<EDiscoverySource, 'queued_export_ids'>): string {
  const n = source.queued_export_ids?.length ?? 0;
  return n ? ` · ${n} more ${n === 1 ? 'export' : 'exports'} queued` : '';
}

/** The single status line under an expanded source, for every state. */
export function sourceStatusLine(
  source: Pick<EDiscoverySource, 'status' | 'error' | 'files'>
    & Partial<Pick<EDiscoverySource, 'search_run' | 'system' | 'queued_export_ids'>>,
): string {
  const total = source.files.length;
  const done = source.files.filter((f) => f.status === 'ingested').length;
  switch (source.status) {
    case 'searching':
      return searchRunLine(source.search_run, EDISCOVERY_SYSTEM_LABELS[source.system ?? ''] ?? source.system ?? 'the system');
    case 'pending':
    case 'preparing':
      return 'Preparing export…' + queuedSuffix(source);
    case 'ready':
      return `Importing ${total} ${total === 1 ? 'file' : 'files'}…` + queuedSuffix(source);
    case 'ingesting':
      return (total ? `Importing files… ${done}/${total}` : 'Importing files…') + queuedSuffix(source);
    case 'ingested':
      return `Imported ${done} of ${total} ${total === 1 ? 'file' : 'files'}`;
    case 'failed':
      return source.error || 'Failed to prepare';
  }
}

/** One line under the export checklist saying what "Open case" will import. */
export function selectionHint(count: number, systemLabel: string): string {
  if (count === 0) return `Tick the exports to import from ${systemLabel}.`;
  return `${count} ${count === 1 ? 'export' : 'exports'} will be imported when you open the case.`;
}

export const EDISCOVERY_SYSTEM_LABELS: Record<string, string> = {
  purview: 'Purview',
  jatheon: 'Jatheon',
  mock: 'Mock (demo)',
};

/** One eDiscovery import running for a case, as the documents panel shows it. */
export interface ImportProgress {
  id: string;
  /** "Importing exports from Purview…" */
  title: string;
  /** "Preparing export…", "3 of 5 files imported", or the error. */
  detail: string;
  /** 0-100, or null while the size isn't known yet (indeterminate bar). */
  percent: number | null;
  failed: boolean;
}

const RUNNING = new Set(['searching', 'pending', 'preparing', 'ready', 'ingesting']);

/**
 * The imports to show on the case page (FB-1007 #4): every source still being
 * prepared or ingested, and failed ones so the failure isn't silent. Finished
 * sources drop out; their documents are in the list by then.
 *
 * A search RedactIt runs in the system (sc-11767) gets its own card, next to
 * the import card when an import from the same case is going at the same time,
 * and stays as a failed card when the search failed but the source went on to
 * import the exports ticked meanwhile. Exports queued behind an import are
 * counted on the import card: a search never holds exports back.
 */
export function importProgress(sources: EDiscoverySource[]): ImportProgress[] {
  return sources.flatMap((s) => {
    const system = EDISCOVERY_SYSTEM_LABELS[s.system] ?? s.system;
    const cards: ImportProgress[] = [];
    const run = s.search_run;
    if (s.status === 'searching' || searchRunActive(run)) {
      const { step, line } = searchRunStep(run);
      cards.push({
        id: s.status === 'searching' ? s.id : `${s.id}:search`,
        title: `${system} search · step ${step} of 3`,
        detail: line,
        percent: null,
        failed: false,
      });
    } else if (run?.stage === 'failed' && run.error && s.status !== 'failed') {
      cards.push({ id: `${s.id}:search`, title: `${system} search failed`, detail: run.error, percent: null, failed: true });
    }
    if (s.status === 'failed') {
      cards.push({
        id: s.id,
        title: run?.stage === 'failed' && run.error === s.error ? `${system} search failed` : `Import from ${system} failed`,
        detail: s.error || 'The export could not be imported.',
        percent: null,
        failed: true,
      });
    } else if (RUNNING.has(s.status) && s.status !== 'searching') {
      const total = s.files.length;
      const done = s.files.filter((f) => f.status === 'ingested' || f.status === 'failed').length;
      const preparing = s.status === 'pending' || s.status === 'preparing';
      cards.push({
        id: s.id,
        title: `Importing exports from ${system}…`,
        detail: (preparing
          ? `Preparing ${s.case_name ? `“${s.case_name}”` : 'the export'}…`
          : `${done} of ${total} ${total === 1 ? 'file' : 'files'} imported`) + queuedSuffix(s),
        percent: preparing || total === 0 ? null : Math.round((done / total) * 100),
        failed: false,
      });
    }
    return cards;
  });
}
